import React from "react";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Checkbox from "@mui/material/Checkbox";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { getformattedCellValue } from "../../utils/helpers";
import { black80, white } from "../../theme/color.constants";

const StyledSpan = styled("span")({
  border: 0,
  clip: "rect(0 0 0 0)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  top: 20,
  width: 1,
});

const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
  "&:hover .MuiTableSortLabel-icon, &:focus .MuiTableSortLabel-icon": {
    opacity: 1,
  },
  "&:focus, &:hover": {
    color: theme.palette.mode === "light" ? black80 : white,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "& span[tabindex='0']:focus, & span[tabindex='0']:hover": {
    color: theme.palette.mode === "light" ? black80 : white,
  },
}));

const headerToolTipStyles = {
  "& > .header-cell-content": { display: "inline-flex", alignItems: "center" },
};
function EnhancedTableHead(props) {
  const {
    columns,
    onSelectAllClick = () => {},
    order,
    orderBy,
    numSelected = 0,
    rowCount = 0,
    maxSelectRow = Infinity,
    onRequestSort,
    rowSelect = false,
    req,
    hasSubTable = false,
    controlRenderer = () => <TableCell />,
    rowsInfo,
    readonly = false,
  } = props;
  const createSortHandler = (property, defaultSort) => (event) => {
    onRequestSort(event, property, defaultSort);
  };
  const sortKey = String(req.sort).replace(/-/g, "");
  const filterSort = columns.filter((item) => item.sortBy === sortKey);
  const filterSortSystemName = filterSort[0] ? filterSort[0].systemName : null;

  const getDisplayName = (headCell) => {
    const { displayName, condition, dynamicDisplayName } = headCell;
    return getformattedCellValue(
      displayName,
      condition,
      dynamicDisplayName,
      rowsInfo
    );
  };
  return (
    <TableHead>
      <TableRow>
        {rowSelect && (
          <TableCell padding="checkbox" scope="col" aria-label="Select">
            {maxSelectRow === Infinity && (
              <Checkbox
                color="primary"
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                slotProps={{ input: { "aria-label": "select all rows" } }}
                disabled={readonly}
              />
            )}
          </TableCell>
        )}
        {hasSubTable && (
          <TableCell scope="col" padding="checkbox" aria-label="Expand">
            {controlRenderer()}
          </TableCell>
        )}
        {columns.map((headCell) => (
          <StyledTableCell
            key={headCell.systemName + headCell.displayName}
            align={headCell.numeric ? "right" : "left"}
            padding="normal"
            sortDirection={String(req.sort).includes("-") ? "desc" : "asc"}
            scope="col"
            sx={{ ...(headCell.headerTooltip && headerToolTipStyles) }}
          >
            <span className="header-cell-content">
              {headCell.isSortable ? (
                <StyledTableSortLabel
                  active={filterSortSystemName === headCell.systemName}
                  direction={
                    String(req.sort).includes("-") &&
                    filterSortSystemName === headCell.systemName
                      ? "desc"
                      : "asc"
                  }
                  onClick={createSortHandler(
                    headCell.systemName,
                    filterSortSystemName === headCell.systemName
                  )}
                >
                  {getDisplayName(headCell)}
                  {orderBy === headCell.systemName ? (
                    <StyledSpan>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </StyledSpan>
                  ) : null}
                </StyledTableSortLabel>
              ) : (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                <span tabIndex={0}>{getDisplayName(headCell)}</span>
              )}
              {headCell.headerTooltip && (
                <Tooltip title={headCell.headerTooltip}>
                  <InfoOutlinedIcon
                    tabIndex={0}
                    sx={(theme) => ({
                      fontSize: "1.125rem",
                      ml: "4px",
                      cursor: "default",
                      color: theme.palette.mode === "light" ? black80 : white,
                    })}
                  />
                </Tooltip>
              )}
            </span>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  columns: PropTypes.oneOfType([PropTypes.array]).isRequired,
  numSelected: PropTypes.number,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number,
  maxSelectRow: PropTypes.number,
  rowSelect: PropTypes.bool,
  hasSubTable: PropTypes.bool,
  controlRenderer: PropTypes.func,
  rowsInfo: PropTypes.shape({}).isRequired,
  readonly: PropTypes.bool,
};

EnhancedTableHead.defaultProps = {
  rowSelect: false,
  numSelected: 0,
  rowCount: 0,
  maxSelectRow: Infinity,
  onSelectAllClick: () => {},
  hasSubTable: false,
  controlRenderer: () => <TableCell />,
  readonly: false,
};

export default EnhancedTableHead;
